import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import MDEditor from '@uiw/react-md-editor';
import Select from 'react-select';
import { toast } from 'react-toastify';
import StateCard from '../../components/StateCard';
import MemberEditor from '../../components/MemberEditor';
import { groupCarriersByName } from '../../utils/carrierHelpers';

function AgentForm({ token }) {
  const navigate = useNavigate();
  const { email, locationId } = useParams();
  const selectedCampaign = localStorage.getItem('selectedCampaign') || 'medicare';
  const isAcaCampaign = selectedCampaign.toLowerCase().includes('aca') || selectedCampaign.toLowerCase().includes('obamacare') || selectedCampaign.toLowerCase().includes('marketplace');
  const isMedicareCampaign = !isAcaCampaign;
  const isGroupEdit = !!locationId;
  const isIndividualEdit = !!email;
  const isEdit = isGroupEdit || isIndividualEdit;
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    npn: '',
    phone: '',
    internalPhone: '',
    healthSherpaMarketingLink: '',
    dentalInsuranceLink: '',
    lifeInsuranceLink: '',
    autoInsuranceLink: '',
    debtReliefLink: '',
    creditServicesLink: '',
    mcaLink: '',
    consent: '# Agent Consent\n\nPlease add your consent information here...',
    nonFfmConsent: '',
    soaConsent: '',
    renewableConsent: '',
    statesWithCarriers: {},
    agentType: 'individual',
    ghlLocationId: '',
    ghlLocationName: '',
    ghlPrivateToken: '',
    ghlFilterTags: [],
    groupMembers: [],
    distributionConfig: {
      enabled: false,
      priority: 1,
      weightage: 1,
      dailyCap: 0,
      monthlyCap: 0,
      totalPurchased: 0,
      dailySent: 0,
      monthlySent: 0,
      totalSent: 0
    }
  });
  const [states, setStates] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [carrierSearchTerm, setCarrierSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [editingState, setEditingState] = useState(null);
  const [showAddState, setShowAddState] = useState(false);
  const [newStateCarriers, setNewStateCarriers] = useState([]);
  const [ghlLocations, setGhlLocations] = useState([]);
  const [loadingGHL, setLoadingGHL] = useState(false);
  const [tokenLocked, setTokenLocked] = useState(true);
  const [testingToken, setTestingToken] = useState(false);
  // ── Email change state ──
  const [emailEditMode, setEmailEditMode] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailChanging, setEmailChanging] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [ghlContactCount, setGhlContactCount] = useState(null);
  const [loadingGHLCount, setLoadingGHLCount] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [initialToken, setInitialToken] = useState(''); // Track initial token
  
  // Member management state
  const [editingMember, setEditingMember] = useState(null);
  const [addingMember, setAddingMember] = useState(false);
  const [memberGHLCounts, setMemberGHLCounts] = useState({}); // Store GHL counts by member email
  const [loadingMemberCounts, setLoadingMemberCounts] = useState(false);

  useEffect(() => {
    fetchStates();
    fetchCarriers();
    fetchGHLLocations();
    if (isGroupEdit && locationId) {
      fetchGroupByLocation(locationId);
    } else if (isIndividualEdit && email) {
      fetchAgent();
    }
  }, [email, locationId]);

  // Fetch available tags when location and token are set (even when locked - just for display)
  useEffect(() => {
    if (formData.ghlLocationId && formData.ghlPrivateToken) {
      fetchAvailableTags();
    }
  }, [formData.ghlLocationId, formData.ghlPrivateToken]);

  const fetchAvailableTags = async () => {
    try {
      setLoadingTags(true);
      const response = await axios.get(
        `/api/admin/ghl/locations/${formData.ghlLocationId}/tags?token=${formData.ghlPrivateToken}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setAvailableTags(response.data.tags || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setAvailableTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  // ⭐ DB-CACHED: GHL counts come from agent.ghlContactCount / member.ghlContactCount
  // stored in DynamoDB.  No live GHL API calls on form load.
  // Counts are seeded via POST /api/admin/sync-ghl-counts and auto-incremented
  // on every lead tracked via /track.
  // (fetchGHLCount and fetchMemberGHLCounts removed — no longer needed)

  const fetchGHLLocations = async () => {
    try {
      setLoadingGHL(true);
      const response = await axios.get('/api/admin/ghl/locations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setGhlLocations(response.data.locations || []);
      }
    } catch (error) {
      console.error('Error fetching GHL locations:', error);
      // Silent fail - GHL integration is optional
    } finally {
      setLoadingGHL(false);
    }
  };

  const fetchAgent = async () => {
    try {
      const response = await axios.get(`/api/admin/agents/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const agent = response.data;
      if (agent) {
        // Ensure consent is always a string
        let consentValue = '# Agent Consent\n\nPlease add your consent information here...';
        if (agent.consent && typeof agent.consent === 'string') {
          consentValue = agent.consent;
        }
        
        setFormData({
          email: agent.email,
          password: '', // For entering NEW password
          currentPasswordHash: agent.password || '', // Store hash for reference only
          name: agent.name,
          npn: agent.npn,
          phone: agent.phone || '',
          internalPhone: agent.internalPhone || '',
          healthSherpaMarketingLink: agent.healthSherpaMarketingLink || '',
          dentalInsuranceLink: agent.dentalInsuranceLink || '',
          lifeInsuranceLink: agent.lifeInsuranceLink || '',
          autoInsuranceLink: agent.autoInsuranceLink || '',
          debtReliefLink: agent.debtReliefLink || '',
          creditServicesLink: agent.creditServicesLink || '',
          mcaLink: agent.mcaLink || '',
          consent: consentValue,
          nonFfmConsent: agent.nonFfmConsent || '',
          soaConsent: agent.soaConsent || '',
          renewableConsent: agent.renewableConsent || '',
          statesWithCarriers: agent.statesWithCarriers || {},
          // ⭐ ACTIVE STATES: null means all active (default)
          activeStates: agent.activeStates || null,
          agentType: agent.agentType || 'individual',
          ghlLocationId: agent.ghlLocationId || '',
          ghlLocationName: agent.ghlLocationName || '',
          ghlPrivateToken: agent.ghlPrivateToken || '',
          ghlFilterTags: agent.ghlFilterTags || [],
          distributionConfig: agent.distributionConfig || {
            enabled: false,
            priority: 1,
            weightage: 1,
            dailyCap: 0,
            monthlyCap: 0,
            totalPurchased: 0,
            dailySent: 0,
            monthlySent: 0,
            totalSent: 0
          }
        });
        setInitialToken(agent.ghlPrivateToken || ''); // Track initial token
        // ⭐ DB-CACHED: seed contact count from stored field (no live GHL call)
        if (agent.ghlContactCount !== undefined && agent.ghlContactCount !== null) {
          setGhlContactCount(agent.ghlContactCount);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching agent:', error);
      toast.error('Error loading agent data');
      setLoading(false);
    }
  };

  const fetchGroupByLocation = async (ghlLocationId) => {
    try {
      const response = await axios.get(`/api/admin/groups/by-location/${ghlLocationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const group = response.data;
      if (group) {
        let consentValue = '# Agent Consent\n\nPlease add your consent information here...';
        if (group.consent && typeof group.consent === 'string') {
          consentValue = group.consent;
        }
        
        // Groups don't have email/name/npn - only locationId
        setFormData({
          email: '', // Groups don't have email
          password: '',
          name: '', // Groups don't have name
          npn: '', // Groups don't have NPN
          consent: consentValue,
          nonFfmConsent: group.nonFfmConsent || '',
          soaConsent: group.soaConsent || '',
          renewableConsent: group.renewableConsent || '',
          statesWithCarriers: group.statesWithCarriers || {},
          agentType: 'group',
          ghlLocationId: group.ghlLocationId || ghlLocationId,
          ghlLocationName: group.ghlLocationName || '',
          ghlPrivateToken: group.ghlPrivateToken || '',
          ghlFilterTags: group.ghlFilterTags || [],
          groupMembers: group.groupMembers || [],
          distributionConfig: group.distributionConfig || {
            enabled: false,
            priority: 1,
            weightage: 1,
            dailyCap: 0,
            monthlyCap: 0,
            totalPurchased: 0,
            dailySent: 0,
            monthlySent: 0,
            totalSent: 0
          }
        });
      }
      // ⭐ DB-CACHED: seed group-level GHL contact count (no live GHL call)
      if (group && group.ghlContactCount !== undefined && group.ghlContactCount !== null) {
        setGhlContactCount(group.ghlContactCount);
      }
      // ⭐ DB-CACHED: seed per-member counts from stored ghlContactCount fields
      if (group && group.groupMembers && group.groupMembers.length > 0) {
        const dbMemberCounts = {};
        group.groupMembers.forEach(m => {
          if (m.ghlContactCount !== undefined && m.ghlContactCount !== null) {
            dbMemberCounts[m.email] = m.ghlContactCount;
          }
        });
        setMemberGHLCounts(dbMemberCounts);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching group:', error);
      toast.error('Error loading group data');
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get('/api/admin/states', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await axios.get('/api/admin/carriers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarriers(response.data);
    } catch (error) {
      console.error('Error fetching carriers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for groups
    if (formData.agentType === 'group' && !formData.ghlLocationId) {
      toast.error('GHL Location is required for groups');
      return;
    }
    
    // Cap validation
    const totalPurchased = formData.distributionConfig?.totalPurchased || 0;
    const dailyCap = formData.distributionConfig?.dailyCap || 0;
    const monthlyCap = formData.distributionConfig?.monthlyCap || 0;
    
    if (formData.agentType === 'individual') {
      // Individual validation
      if (dailyCap > totalPurchased) {
        toast.error(`Daily cap (${dailyCap}) cannot be greater than total purchased (${totalPurchased})`);
        return;
      }
      if (monthlyCap > totalPurchased) {
        toast.error(`Monthly cap (${monthlyCap}) cannot be greater than total purchased (${totalPurchased})`);
        return;
      }
    } else if (formData.agentType === 'group' && formData.groupMembers && formData.groupMembers.length > 0) {
      // Group validation - sum of member caps
      const totalMemberDailyCap = formData.groupMembers.reduce((sum, m) => sum + (m.dailyCap || 0), 0);
      const totalMemberMonthlyCap = formData.groupMembers.reduce((sum, m) => sum + (m.monthlyCap || 0), 0);
      
      if (totalMemberDailyCap > totalPurchased) {
        toast.error(`Sum of member daily caps (${totalMemberDailyCap}) cannot be greater than total purchased (${totalPurchased})`);
        return;
      }
      if (totalMemberMonthlyCap > totalPurchased) {
        toast.error(`Sum of member monthly caps (${totalMemberMonthlyCap}) cannot be greater than total purchased (${totalPurchased})`);
        return;
      }
    }
    
    console.log('🔍 DEBUG - Current formData.groupMembers:', formData.groupMembers);
    console.log('🔍 DEBUG - Member count:', formData.groupMembers?.length || 0);
    
    setSaving(true);

    try {
      let url, method, dataToSave;
      
      if (formData.agentType === 'group') {
        // Groups ALWAYS use the same endpoint/payload (both create and edit)
        url = `/api/admin/groups/location/${encodeURIComponent(formData.ghlLocationId)}`;
        method = 'put';
        dataToSave = {
          agentType: 'group',
          ghlLocationId: formData.ghlLocationId,
          ghlLocationName: formData.ghlLocationName,
          ghlPrivateToken: formData.ghlPrivateToken,
          ghlFilterTags: formData.ghlFilterTags || [],
          consent: formData.consent,
          nonFfmConsent: formData.nonFfmConsent || '',
          soaConsent: formData.soaConsent || '',
          renewableConsent: formData.renewableConsent || '',
          groupMembers: formData.groupMembers || [],
          distributionConfig: formData.distributionConfig
        };
      } else {
        // Individual agents
        url = isEdit 
          ? `/api/admin/agents/${encodeURIComponent(formData.email)}`
          : '/api/admin/agents';
        method = isEdit ? 'put' : 'post';
        dataToSave = isEdit ? {
          email: formData.email,
          name: formData.name,
          npn: formData.npn,
          phone: formData.phone || '',
          internalPhone: formData.internalPhone || '',
          healthSherpaMarketingLink: formData.healthSherpaMarketingLink || '',
          dentalInsuranceLink: formData.dentalInsuranceLink || '',
          lifeInsuranceLink: formData.lifeInsuranceLink || '',
          autoInsuranceLink: formData.autoInsuranceLink || '',
          debtReliefLink: formData.debtReliefLink || '',
          creditServicesLink: formData.creditServicesLink || '',
          mcaLink: formData.mcaLink || '',
          consent: formData.consent,
          nonFfmConsent: formData.nonFfmConsent || '',
          soaConsent: formData.soaConsent || '',
          renewableConsent: formData.renewableConsent || '',
          agentType: formData.agentType,
          ghlLocationId: formData.ghlLocationId,
          ghlLocationName: formData.ghlLocationName,
          ghlPrivateToken: formData.ghlPrivateToken,
          ghlFilterTags: formData.ghlFilterTags || [],
          distributionConfig: formData.distributionConfig,
          activeStates: formData.activeStates ?? null,
          ...(formData.password && { password: formData.password })
        } : formData;
      }
      
      const response = await axios[method](url, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isEdit ? `${formData.agentType === 'group' ? 'Group' : 'Agent'} updated successfully!` : `${formData.agentType === 'group' ? 'Group' : 'Agent'} created successfully!`);
      
      if (!isEdit) {
        // After creating, go back to agents list
        navigate('/admin/agents');
      } else if (formData.agentType === 'group') {
        fetchGroupByLocation(formData.ghlLocationId);
      } else {
        fetchAgent();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving data');
    } finally {
      setSaving(false);
    }
  };

  const updateStateCarriers = async (state, carriers) => {
    const updatedData = {
      ...formData,
      statesWithCarriers: {
        ...formData.statesWithCarriers,
        [state]: carriers
      }
    };
    
    const url = `/api/admin/agents/${encodeURIComponent(formData.email)}`;
    await axios.put(url, updatedData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    setFormData(updatedData);
    toast.success('State carriers updated successfully!');
  };

  const deleteState = async (state) => {
    const newStates = { ...formData.statesWithCarriers };
    delete newStates[state];
    
    const updatedData = {
      ...formData,
      statesWithCarriers: newStates
    };
    
    const url = `/api/admin/agents/${encodeURIComponent(formData.email)}`;
    await axios.put(url, updatedData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    setFormData(updatedData);
    toast.success('State removed successfully!');
  };

  // ⭐ ACTIVE STATES — toggle a single state on/off for individual agents
  // Uses dedicated lightweight endpoint (no full agent payload needed)
  const toggleStateActive = async (state) => {
    const allStates = Object.keys(formData.statesWithCarriers);
    // Current activeStates: null means all active
    const current = formData.activeStates || allStates;
    let updated;
    if (current.includes(state)) {
      updated = current.filter(s => s !== state);
    } else {
      updated = [...current, state];
    }
    // If all states are active again, store null (default)
    const toSave = updated.length === allStates.length ? null : updated;
    try {
      await axios.put(
        `/api/admin/agents/${encodeURIComponent(formData.email)}/active-states`,
        { activeStates: toSave },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormData(prev => ({ ...prev, activeStates: toSave }));
      toast.success(`State ${state} ${updated.includes(state) ? 'resumed ✅' : 'paused ⏸'}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update active states');
    }
  };

  const addNewState = async () => {
    if (selectedState && newStateCarriers.length > 0) {
      const updatedData = {
        ...formData,
        statesWithCarriers: {
          ...formData.statesWithCarriers,
          [selectedState.value]: newStateCarriers
        }
      };
      
      try {
        if (isEdit) {
          const url = `/api/admin/agents/${encodeURIComponent(formData.email)}`;
          await axios.put(url, updatedData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('State added successfully!');
        }
        
        setFormData(updatedData);
        setSelectedState(null);
        setNewStateCarriers([]);
        setShowAddState(false);
        setCarrierSearchTerm('');
        
        if (isEdit) {
          fetchAgent();
        }
      } catch (error) {
        toast.error('Error adding state');
      }
    }
  };

  const stateOptions = states
    .filter(s => !formData.statesWithCarriers[s.code])
    .map(s => ({ value: s.code, label: `${s.name} (${s.code})` }));
  
  const groupedAllCarriers = groupCarriersByName(carriers);
  const _selectedStateCode = selectedState?.value || '';
  const carrierOptions = groupedAllCarriers
    .filter(group => {
      // Hide carriers that mention a different US state in their name
      if (_selectedStateCode && group.name) {
        const n = group.name.toLowerCase();
        const otherStateNames = ['alabama','alaska','arizona','arkansas','california','colorado','connecticut','delaware','florida','georgia','hawaii','idaho','illinois','indiana','iowa','kansas','kentucky','louisiana','maine','maryland','massachusetts','michigan','minnesota','mississippi','missouri','montana','nebraska','nevada','new hampshire','new jersey','new mexico','new york','north carolina','north dakota','ohio','oklahoma','oregon','pennsylvania','rhode island','south carolina','south dakota','tennessee','texas','utah','vermont','virginia','washington','west virginia','wisconsin','wyoming'];
        const stateCodeToName = {AL:'alabama',AK:'alaska',AZ:'arizona',AR:'arkansas',CA:'california',CO:'colorado',CT:'connecticut',DE:'delaware',FL:'florida',GA:'georgia',HI:'hawaii',ID:'idaho',IL:'illinois',IN:'indiana',IA:'iowa',KS:'kansas',KY:'kentucky',LA:'louisiana',ME:'maine',MD:'maryland',MA:'massachusetts',MI:'michigan',MN:'minnesota',MS:'mississippi',MO:'missouri',MT:'montana',NE:'nebraska',NV:'nevada',NH:'new hampshire',NJ:'new jersey',NM:'new mexico',NY:'new york',NC:'north carolina',ND:'north dakota',OH:'ohio',OK:'oklahoma',OR:'oregon',PA:'pennsylvania',RI:'rhode island',SC:'south carolina',SD:'south dakota',TN:'tennessee',TX:'texas',UT:'utah',VT:'vermont',VA:'virginia',WA:'washington',WV:'west virginia',WI:'wisconsin',WY:'wyoming'};
        const currentStateLower = stateCodeToName[_selectedStateCode] || '';
        for (const sn of otherStateNames) {
          if (sn !== currentStateLower && n.includes(sn)) return false;
        }
      }
      const allSelected = group.carrierIds.every(carrierId =>
        newStateCarriers.some(nc => nc.CarrierId === carrierId)
      );
      return !allSelected;
    })
    .map(group => ({
      value: group,
      label: `${group.name} (${group.carrierIds.length})`
    }));

  if (loading) return <div className="loading">Loading agent...</div>;

  return (
    <div className="container">
      <div className="page-header">
        <h2>{isEdit ? 'Edit Agent' : 'Create New Agent'}</h2>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          {isEdit && (
            <label style={{ 
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              position: 'relative'
            }}>
              <input
                type="checkbox"
                checked={formData.distributionConfig?.enabled || false}
                onChange={() => setFormData({
                  ...formData,
                  distributionConfig: {
                    ...formData.distributionConfig,
                    enabled: !formData.distributionConfig?.enabled
                  }
                })}
                style={{ display: 'none' }}
              />
              <div style={{
                width: '52px',
                height: '28px',
                background: formData.distributionConfig?.enabled ? '#10b981' : '#9ca3af',
                borderRadius: '14px',
                position: 'relative',
                transition: 'background 0.3s',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: formData.distributionConfig?.enabled ? '26px' : '2px',
                  transition: 'left 0.3s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
              </div>
              <span style={{ 
                marginLeft: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: formData.distributionConfig?.enabled ? '#10b981' : '#6b7280'
              }}>
                {formData.distributionConfig?.enabled ? 'Active' : 'Inactive'}
              </span>
            </label>
          )}
          <button className="btn btn-secondary" onClick={() => navigate('/admin/agents')}>
            ← Back
          </button>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="agent-form">
        <div className="form-row">
          <div className="form-group">
            <label>Agent Type</label>
            <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="individual"
                  checked={formData.agentType === 'individual'}
                  onChange={(e) => setFormData({ ...formData, agentType: e.target.value })}
                  style={{ marginRight: '8px' }}
                />
                Individual
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="group"
                  checked={formData.agentType === 'group'}
                  onChange={(e) => setFormData({ ...formData, agentType: e.target.value })}
                  style={{ marginRight: '8px' }}
                />
                Group
              </label>
            </div>
          </div>

          <div className="form-group" style={{margin: 0}}>
            <label>
              GHL Subaccount {formData.agentType === 'group' ? '(Required for Groups) *' : '(Optional for Individuals)'} {loadingGHL && '⏳'}
            </label>
            <Select
              value={formData.ghlLocationId ? {
                value: formData.ghlLocationId,
                label: formData.ghlLocationName || ghlLocations.find(l => l.locationId === formData.ghlLocationId)?.locationName || formData.ghlLocationId
              } : null}
              onChange={(selected) => {
                setFormData({
                  ...formData,
                  ghlLocationId: selected ? selected.value : '',
                  ghlLocationName: selected ? selected.label : ''
                });
              }}
              options={[
                { value: '', label: '-- None --' },
                ...ghlLocations.map(location => ({
                  value: location.locationId,
                  label: location.locationName
                }))
              ]}
              placeholder="Search for a GHL subaccount..."
              isClearable={formData.agentType !== 'group'}
              isSearchable
              isDisabled={loadingGHL}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  fontSize: '14px',
                  borderColor: formData.agentType === 'group' && !formData.ghlLocationId ? '#ef4444' : base.borderColor
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999
                })
              }}
            />
            <small style={{ display: 'block', marginTop: '4px', color: formData.agentType === 'group' && !formData.ghlLocationId ? '#dc2626' : '#6b7280', fontWeight: formData.agentType === 'group' && !formData.ghlLocationId ? '600' : '400' }}>
              {formData.agentType === 'group' && !formData.ghlLocationId
                ? '* GHL Location is REQUIRED for groups (used as primary identifier)'
                : formData.agentType === 'group' && formData.ghlLocationId
                ? 'GHL Location set (used as primary identifier for this group)'
                : 'Optional: Select a GHL location to enable dynamic contact counting'
              }
            </small>
          </div>
        </div>

        {formData.ghlLocationId && (
          <div className="form-row">
            <div className="form-group">
              <label>GHL Private Integration Token (PIT) *</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type={tokenLocked ? "password" : "text"}
                    value={formData.ghlPrivateToken}
                    onChange={(e) => setFormData({ ...formData, ghlPrivateToken: e.target.value })}
                    placeholder={tokenLocked ? "••••••••••••••••" : "Enter sub-account Private Integration Token..."}
                    disabled={tokenLocked}
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '13px',
                      width: '100%',
                      paddingRight: '40px',
                      background: tokenLocked ? '#f3f4f6' : 'white',
                      cursor: tokenLocked ? 'not-allowed' : 'text'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setTokenLocked(!tokenLocked)}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px',
                      color: tokenLocked ? '#6b7280' : '#4f46e5'
                    }}
                    title={tokenLocked ? "Click to unlock and edit" : "Click to lock"}
                  >
                    {tokenLocked ? '🔒' : '🔓'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    if (!formData.ghlPrivateToken || !formData.ghlLocationId) {
                      toast.error('Please enter a token first');
                      return;
                    }
                    
                    setTestingToken(true);
                    setTestResult(null);
                    
                    try {
                      const response = await axios.post('/api/admin/ghl/test-token', {
                        ghlLocationId: formData.ghlLocationId,
                        ghlPrivateToken: formData.ghlPrivateToken,
                        ghlFilterTags: formData.ghlFilterTags || []
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      
                      if (response.data.success) {
                        const tags = formData.ghlFilterTags && formData.ghlFilterTags.length > 0;
                        setTestResult({
                          success: true,
                          count: response.data.contactCount,
                          withTags: tags
                        });
                        const message = tags 
                          ? `Token valid! Found ${response.data.contactCount} contacts with tags: ${formData.ghlFilterTags.join(', ')}`
                          : `Token valid! Found ${response.data.contactCount} total contacts`;
                        toast.success(message);
                      }
                    } catch (error) {
                      setTestResult({
                        success: false,
                        error: error.response?.data?.error || 'Invalid token'
                      });
                      toast.error(error.response?.data?.error || 'Token test failed');
                    } finally {
                      setTestingToken(false);
                    }
                  }}
                  disabled={testingToken || !formData.ghlPrivateToken || tokenLocked}
                  className="btn btn-sm btn-primary"
                  style={{
                    whiteSpace: 'nowrap',
                    minWidth: '80px',
                    opacity: (!formData.ghlPrivateToken || tokenLocked) ? 0.5 : 1
                  }}
                >
                  {testingToken ? '⏳ Testing...' : '🧪 Test'}
                </button>
              </div>
              <small style={{ display: 'block', marginTop: '4px', color: '#6b7280' }}>
                Get this from Settings → Integrations in the selected sub-account
              </small>
              {testResult && (
                <div style={{ 
                  marginTop: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  background: testResult.success ? '#d1fae5' : '#fee2e2',
                  color: testResult.success ? '#065f46' : '#991b1b',
                  border: `1px solid ${testResult.success ? '#6ee7b7' : '#fca5a5'}`
                }}>
                  {testResult.success ? (
                    <>✓ Token verified! Contact count: {testResult.count}</>
                  ) : (
                    <>✗ Token invalid: {testResult.error}</>
                  )}
                </div>
              )}
              {formData.ghlPrivateToken && !testResult && formData.ghlPrivateToken !== initialToken && (
                <div style={{ fontSize: '12px', color: '#f59e0b', marginTop: '4px', fontWeight: '500' }}>
                  ⚠️ Token not tested - click Test button to verify
                </div>
              )}
            </div>

            {formData.ghlPrivateToken && !tokenLocked && (
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>Filter Tags (Optional) {loadingTags && '⏳'}</label>
                <Select
                  isMulti
                  value={formData.ghlFilterTags ? formData.ghlFilterTags.map(tag => ({ value: tag, label: tag })) : []}
                  onChange={(selected) => {
                    setFormData({
                      ...formData,
                      ghlFilterTags: selected ? selected.map(s => s.value) : []
                    });
                  }}
                  options={availableTags.map(tag => ({ value: tag, label: tag }))}
                  placeholder={loadingTags ? "Loading tags..." : "Select tags to filter contacts (e.g., medicare, aca)"}
                  isDisabled={loadingTags}
                  isClearable
                  isSearchable
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      fontSize: '14px'
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999
                    })
                  }}
                />
                <small style={{ display: 'block', marginTop: '4px', color: '#6b7280' }}>
                  {availableTags.length > 0 ? (
                    <>Filter contact count to only include contacts with these tags (OR logic). Found {availableTags.length} available tags.</>
                  ) : (
                    <>No tags fetched yet. Unlock the token above to load available tags.</>
                  )}
                </small>
                {formData.ghlFilterTags && formData.ghlFilterTags.length > 0 && (
                  <div style={{ 
                    marginTop: '8px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: '#eff6ff',
                    color: '#1e40af',
                    border: '1px solid #bfdbfe'
                  }}>
                    ℹ️ Contact count will only include contacts tagged with: <strong>{formData.ghlFilterTags.join(' OR ')}</strong>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="form-row">
        </div>

        {/* Conditional rendering: Different fields for groups vs individuals */}
        {formData.agentType === 'group' ? (
          /* GROUP - NO email/name/NPN, just GHL location (members have those) */
          <>

            {/* Group Members Section - Full Management (available during creation and editing) */}
            {(isEdit || !isEdit) && (
              <div style={{
                padding: '24px',
                background: '#faf5ff',
                border: '2px solid #e9d5ff',
                borderRadius: '12px',
                marginBottom: '24px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 14px',
                    background: 'linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    <span>👥</span>
                    <span>GROUP MEMBERS</span>
                  </span>
                  {!addingMember && !editingMember && (
                    <button
                      type="button"
                      onClick={() => setAddingMember(true)}
                      style={{
                        padding: '8px 16px',
                        background: '#7c3aed',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      + Add Member
                    </button>
                  )}
                </div>
                
                {/* Add Member Form */}
                {addingMember && (
                  <MemberEditor
                    member={null}
                    onChange={(memberData) => {
                      // Only add if Save was clicked (memberData not null)
                      if (memberData) {
                        // ⭐ Check for duplicate email within this group's members
                        if (memberData.email) {
                          const existingEmails = (formData.groupMembers || []).map(m => (m.email || '').toLowerCase());
                          if (existingEmails.includes(memberData.email.toLowerCase())) {
                            toast.error(`A member with email "${memberData.email}" already exists in this group.`);
                            return;
                          }
                        }
                        const updatedMembers = [...(formData.groupMembers || []), memberData];
                        setFormData({ ...formData, groupMembers: updatedMembers });
                        toast.success('Member added! Click "Create Group" at bottom to save.');
                      }
                    }}
                    onClose={() => setAddingMember(false)}
                    states={states}
                    carriers={carriers}
                    isNew={true}
                    availableTags={availableTags}
                    ghlLocationId={formData.ghlLocationId}
                    ghlPrivateToken={formData.ghlPrivateToken}
                    authToken={token}
                  />
                )}

                {/* Edit Member Form */}
                {editingMember && (
                  <MemberEditor
                    member={{
                      ...editingMember,
                      ghlCount: memberGHLCounts[editingMember.email]
                    }}
                    onChange={(memberData) => {
                      // Only update if Save was clicked (memberData not null)
                      if (memberData) {
                        const updatedMembers = (formData.groupMembers || []).map(m =>
                          m.email === editingMember.email ? memberData : m
                        );
                        setFormData({ ...formData, groupMembers: updatedMembers });
                        toast.success('Member updated! Click "Save Changes" at bottom to persist.');
                      }
                    }}
                    onClose={() => setEditingMember(null)}
                    states={states}
                    carriers={carriers}
                    isNew={false}
                    availableTags={availableTags}
                    ghlLocationId={formData.ghlLocationId}
                    ghlPrivateToken={formData.ghlPrivateToken}
                    authToken={token}
                  />
                )}
                
                {/* Member List */}
                {!addingMember && !editingMember && (
                  formData.groupMembers && formData.groupMembers.length > 0 ? (
                    <div>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#7c3aed',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Current Members ({formData.groupMembers.length}):
                      </div>
                      {formData.groupMembers.map((member, idx) => (
                        <div key={member.email} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          padding: '12px',
                          background: 'white',
                          border: '1px solid #e9d5ff',
                          borderRadius: '6px',
                          marginBottom: idx < formData.groupMembers.length - 1 ? '8px' : '0'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                              {member.name}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                              {member.email} • NPN: {member.npn}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                              Priority: <strong>{member.priority || 1}</strong> | 
                              Weight: <strong>{member.weightage || 1}</strong> | 
                              Daily Cap: <strong>{member.dailyCap || 0}</strong> | 
                              Monthly Cap: <strong>{member.monthlyCap || 0}</strong> |
                              Total Send: <strong>{member.totalSend || 0}</strong>
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                              States: <strong>{Object.keys(member.statesWithCarriers || {}).length}</strong> | 
                              Total Sent: <strong>{member.totalSent || 0}</strong> {member.totalSend > 0 && `/ ${member.totalSend}`}
                            </div>
                            
                            {/* Lead Statistics Breakdown + GHL Count */}
                            <div style={{
                              marginTop: '8px',
                              padding: '8px',
                              background: '#f0f9ff',
                              borderRadius: '4px',
                              display: 'grid',
                              gridTemplateColumns: member.ghlFilterTag ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)',
                              gap: '8px',
                              fontSize: '11px'
                            }}>
                              <div>
                                <div style={{ color: '#6b7280', fontSize: '10px', marginBottom: '2px' }}>Today</div>
                                <div style={{ fontWeight: '700', color: '#0891b2' }}>{member.dailySent || 0}</div>
                              </div>
                              <div>
                                <div style={{ color: '#6b7280', fontSize: '10px', marginBottom: '2px' }}>Yesterday</div>
                                <div style={{ fontWeight: '700', color: '#0891b2' }}>{member.yesterdaySent || 0}</div>
                              </div>
                              <div>
                                <div style={{ color: '#6b7280', fontSize: '10px', marginBottom: '2px' }}>This Month</div>
                                <div style={{ fontWeight: '700', color: '#0891b2' }}>{member.monthlySent || 0}</div>
                              </div>
                              <div>
                                <div style={{ color: '#6b7280', fontSize: '10px', marginBottom: '2px' }}>
                                  {member.ghlContactCount !== undefined && member.ghlContactCount !== null ? 'Total (GHL)' : 'Total (DB)'}
                                </div>
                                <div style={{ fontWeight: '700', color: '#0891b2' }}>
                                  {member.ghlContactCount !== undefined && member.ghlContactCount !== null
                                    ? member.ghlContactCount
                                    : (member.totalSent || 0)}
                                </div>
                              </div>
                              {member.ghlFilterTag && (
                                <div style={{
                                  background: (member.ghlContactCount !== undefined && member.ghlContactCount !== null) ? '#dbeafe' : '#f3f4f6',
                                  padding: '4px',
                                  borderRadius: '3px',
                                  border: (member.ghlContactCount !== undefined && member.ghlContactCount !== null) ? '1px solid #7dd3fc' : '1px solid #d1d5db'
                                }}>
                                  <div style={{ color: '#0c4a6e', fontSize: '9px', marginBottom: '2px', fontWeight: '600' }}>
                                    GHL {member.ghlFilterTag}
                                  </div>
                                  <div style={{ 
                                    fontWeight: '700', 
                                    color: '#0369a1',
                                    fontSize: '13px'
                                  }}>
                                    {member.ghlContactCount !== undefined && member.ghlContactCount !== null
                                      ? member.ghlContactCount
                                      : (memberGHLCounts[member.email] ?? '-')}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              type="button"
                              onClick={() => setEditingMember(member)}
                              style={{
                                padding: '6px 12px',
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm(`Delete member ${member.name}?`)) {
                                  // Update local state (same for both create and edit)
                                  const updatedMembers = (formData.groupMembers || []).filter(
                                    m => m.email !== member.email
                                  );
                                  setFormData({ ...formData, groupMembers: updatedMembers });
                                  toast.success('Member removed from group!');
                                }
                              }}
                              style={{
                                padding: '6px 12px',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      background: 'white',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e9d5ff',
                      fontSize: '13px',
                      color: '#6b7280',
                      textAlign: 'center'
                    }}>
                      No members yet. Click "+ Add Member" to add your first member.
                    </div>
                  )
                )}
              </div>
            )}
          </>
        ) : (
          /* INDIVIDUAL AGENT FIELDS */
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={isEdit}
                  style={{
                    background: isEdit ? '#f3f4f6' : 'white',
                    cursor: isEdit ? 'not-allowed' : 'text',
                    color: isEdit ? '#6b7280' : 'inherit'
                  }}
                />
                {/* ── Change Email (Edit Mode) ── */}
                {isEdit ? (
                  <div style={{ marginTop: '6px' }}>
                    {!emailEditMode ? (
                      <button
                        type="button"
                        onClick={() => { setEmailEditMode(true); setNewEmailInput(formData.email || ''); }}
                        style={{
                          background: 'none',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          padding: '4px 12px',
                          fontSize: '12px',
                          color: '#6b7280',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}
                      >
                        ✏️ Change Email
                      </button>
                    ) : (
                      <div style={{
                        background: '#fef3c7',
                        border: '1px solid #f59e0b',
                        borderRadius: '8px',
                        padding: '12px',
                        marginTop: '4px'
                      }}>
                        <div style={{ fontSize: '12px', color: '#92400e', fontWeight: '600', marginBottom: '8px' }}>
                          ⚠️ Changing email migrates all records to the new address. Agent must log in with the new email afterward.
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            type="email"
                            value={newEmailInput}
                            onChange={(e) => setNewEmailInput(e.target.value)}
                            placeholder="Enter new email address..."
                            style={{
                              flex: '1 1 200px',
                              padding: '7px 12px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              fontSize: '14px'
                            }}
                          />
                          <button
                            type="button"
                            disabled={
                              emailChanging ||
                              !newEmailInput ||
                              !newEmailInput.includes('@') ||
                              newEmailInput.toLowerCase() === (formData.email || '').toLowerCase()
                            }
                            onClick={async () => {
                              if (!window.confirm(
                                `Change email from "${formData.email}" to "${newEmailInput}"?\n\nThis cannot be undone. The agent must log in with the new email.`
                              )) return;
                              setEmailChanging(true);
                              try {
                                const resp = await axios.post(
                                  `/api/admin/agents/${encodeURIComponent(formData.email)}/change-email`,
                                  { newEmail: newEmailInput },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                if (resp.data.success) {
                                  toast.success(`✅ Email changed to ${newEmailInput}`);
                                  setEmailEditMode(false);
                                  navigate(`/admin/agents/edit/${encodeURIComponent(newEmailInput)}`);
                                }
                              } catch (err) {
                                toast.error(err.response?.data?.error || 'Failed to change email');
                              } finally {
                                setEmailChanging(false);
                              }
                            }}
                            style={{
                              background: emailChanging ? '#9ca3af' : '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '7px 16px',
                              fontSize: '13px',
                              cursor: emailChanging ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {emailChanging ? '⏳ Changing...' : '✓ Confirm'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEmailEditMode(false); setNewEmailInput(''); }}
                            style={{
                              background: '#f3f4f6',
                              color: '#374151',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              padding: '7px 12px',
                              fontSize: '13px',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <small style={{ display: 'block', marginTop: '4px', color: '#6b7280' }}>
                    Email will be used as login username
                  </small>
                )}
              </div>
              
              <div className="form-group" style={{margin: 0}}>
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  {isEdit ? 'New Password (leave blank to keep current)' : 'Password *'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={isEdit ? "Enter new password to change" : "Enter password"}
                  required={!isEdit}
                />
                {isEdit && formData.currentPasswordHash && (
                  <div style={{ 
                    marginTop: '8px',
                    padding: '8px 12px',
                    background: '#fef3c7',
                    borderRadius: '6px',
                    border: '1px solid #f59e0b'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#92400e',
                      fontWeight: '600',
                      marginBottom: '4px'
                    }}>
                      Current Password Hash (Admin View Only):
                    </div>
                    <div style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '11px',
                      color: '#451a03',
                      wordBreak: 'break-all',
                      background: '#fffbeb',
                      padding: '4px 6px',
                      borderRadius: '4px'
                    }}>
                      {formData.currentPasswordHash}
                    </div>
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#92400e',
                      marginTop: '4px',
                      fontStyle: 'italic'
                    }}>
                      ⚠️ This is a bcrypt hash and cannot be decrypted. To change password, enter a new one above.
                    </div>
                  </div>
                )}
              </div>
              
              <div className="form-group" style={{margin: 0}}>
                <label>NPN *</label>
                <input
                  type="text"
                  value={formData.npn}
                  onChange={(e) => setFormData({ ...formData, npn: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Business Phone <span style={{ color: '#ef4444', fontWeight: 700 }}>*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 000-0000"
                />
                <div style={{ marginTop: 6, padding: '8px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, fontSize: 12, color: '#0369a1', lineHeight: 1.5 }}>
                  📱 <strong>Business phone</strong> — shown to clients in consent forms and conversations.
                </div>
              </div>

              <div className="form-group" style={{margin: 0}}>
                <label>
                  Personal Phone <span style={{ fontWeight: 'normal', color: '#6b7280', fontSize: 12 }}>(Optional)</span>
                </label>
                <input
                  type="tel"
                  value={formData.internalPhone || ''}
                  onChange={(e) => setFormData({ ...formData, internalPhone: e.target.value })}
                  placeholder="+1 (555) 000-0000"
                />
                <div style={{ marginTop: 6, padding: '8px 10px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 6, fontSize: 12, color: '#854d0e', lineHeight: 1.5 }}>
                  🔒 <strong>Personal phone</strong> — used for login OTP and password reset only. Never shown to clients.
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{margin: 0}}>
                <label>HealthSherpa Marketing Link (Optional)</label>
                <input
                  type="url"
                  value={formData.healthSherpaMarketingLink}
                  onChange={(e) => setFormData({ ...formData, healthSherpaMarketingLink: e.target.value })}
                  placeholder="https://example.healthsherpa.com/agent123"
                />
                <small style={{ display: 'block', marginTop: '4px', color: '#6b7280' }}>
                  Enter the agent's HealthSherpa marketing link if applicable
                </small>
              </div>
            </div>

            {/* ── Optional Insurance Links ── */}
            <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 12 }}>📋 Optional Insurance Links (shown in lead app)</div>
              <div className="form-row">
                <div className="form-group" style={{margin: '0 0 10px'}}>
                  <label>Dental Insurance Link (Optional)</label>
                  <input
                    type="url"
                    value={formData.dentalInsuranceLink || ''}
                    onChange={(e) => setFormData({ ...formData, dentalInsuranceLink: e.target.value })}
                    placeholder="https://dental-signup-link.com"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{margin: '0 0 10px'}}>
                  <label>Life Insurance Link (Optional)</label>
                  <input
                    type="url"
                    value={formData.lifeInsuranceLink || ''}
                    onChange={(e) => setFormData({ ...formData, lifeInsuranceLink: e.target.value })}
                    placeholder="https://life-insurance-link.com"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{margin: 0}}>
                  <label>Auto Insurance Link (Optional)</label>
                  <input
                    type="url"
                    value={formData.autoInsuranceLink || ''}
                    onChange={(e) => setFormData({ ...formData, autoInsuranceLink: e.target.value })}
                    placeholder="https://auto-insurance-link.com"
                  />
                </div>
              </div>
            </div>

            {/* ── Optional Partner Services Links ── */}
            <div style={{ marginBottom: 16, padding: '12px 14px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#854d0e', marginBottom: 12 }}>🤝 Optional Partner Services Links (shown in lead app)</div>
              <div className="form-row">
                <div className="form-group" style={{margin: '0 0 10px'}}>
                  <label>Debt Relief Link (Optional)</label>
                  <input
                    type="url"
                    value={formData.debtReliefLink || ''}
                    onChange={(e) => setFormData({ ...formData, debtReliefLink: e.target.value })}
                    placeholder="https://debt-relief-link.com"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{margin: '0 0 10px'}}>
                  <label>Credit Services Link (Optional)</label>
                  <input
                    type="url"
                    value={formData.creditServicesLink || ''}
                    onChange={(e) => setFormData({ ...formData, creditServicesLink: e.target.value })}
                    placeholder="https://credit-services-link.com"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{margin: 0}}>
                  <label>MCA (Merchant Cash Advance) Link (Optional)</label>
                  <input
                    type="url"
                    value={formData.mcaLink || ''}
                    onChange={(e) => setFormData({ ...formData, mcaLink: e.target.value })}
                    placeholder="https://mca-link.com"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Consent (Markdown)</label>
              <MDEditor
                value={formData.consent || ''}
                onChange={(value) => setFormData({ ...formData, consent: value || '' })}
                height={200}
              />
            </div>

            {isAcaCampaign && (
              <div className="form-group">
                <label>Non-FFM Consent (Markdown) <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>— ACA Agents Only</span></label>
                <MDEditor
                  value={formData.nonFfmConsent || ''}
                  onChange={(value) => setFormData({ ...formData, nonFfmConsent: value || '' })}
                  height={200}
                />
              </div>
            )}

            {isMedicareCampaign && (
              <div className="form-group">
                <label>SOA Consent (Markdown) <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>— Medicare Agents Only</span></label>
                <MDEditor
                  value={formData.soaConsent || ''}
                  onChange={(value) => setFormData({ ...formData, soaConsent: value || '' })}
                  height={200}
                />
              </div>
            )}

            <div className="form-group">
              <label>Renewable Consent (Markdown) <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>— Shown to leads eligible for renewal</span></label>
              <MDEditor
                value={formData.renewableConsent || ''}
                onChange={(value) => setFormData({ ...formData, renewableConsent: value || '' })}
                height={200}
              />
              <small style={{ display: 'block', marginTop: '4px', color: '#6b7280' }}>
                This consent is shown to returning leads who were previously enrolled through this agent and are eligible for re-enrollment.
              </small>
            </div>
          </>
        )}

        <div className="form-group">
          <div style={{ 
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '2px solid #e5e7eb'
          }}>
            <label style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>
              Lead Distribution Settings
            </label>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Priority (lower = higher priority)</label>
              <input
                type="number"
                min="1"
                value={formData.distributionConfig?.priority || 1}
                onChange={(e) => setFormData({
                  ...formData,
                  distributionConfig: {
                    ...formData.distributionConfig,
                    priority: parseInt(e.target.value) || 1
                  }
                })}
              />
            </div>
            
            <div className="form-group" style={{margin: 0}}>
              <label>Weightage (1-10, higher = more leads)</label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.distributionConfig?.weightage || 1}
                onChange={(e) => setFormData({
                  ...formData,
                  distributionConfig: {
                    ...formData.distributionConfig,
                    weightage: parseInt(e.target.value) || 1
                  }
                })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Daily Cap</label>
              <input
                type="number"
                min="0"
                value={formData.distributionConfig?.dailyCap || 0}
                onChange={(e) => setFormData({
                  ...formData,
                  distributionConfig: {
                    ...formData.distributionConfig,
                    dailyCap: parseInt(e.target.value) || 0
                  }
                })}
              />
            </div>
            
            <div className="form-group" style={{margin: 0}}>
              <label>Monthly Cap</label>
              <input
                type="number"
                min="0"
                value={formData.distributionConfig?.monthlyCap || 0}
                onChange={(e) => setFormData({
                  ...formData,
                  distributionConfig: {
                    ...formData.distributionConfig,
                    monthlyCap: parseInt(e.target.value) || 0
                  }
                })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Total Purchased Opportunities</label>
            <input
              type="number"
              min="0"
              value={formData.distributionConfig?.totalPurchased || 0}
              onChange={(e) => setFormData({
                ...formData,
                distributionConfig: {
                  ...formData.distributionConfig,
                  totalPurchased: parseInt(e.target.value) || 0
                }
              })}
            />
          </div>

          {isEdit && formData.distributionConfig && (
            <div style={{ 
              background: '#f3f4f6', 
              padding: '16px', 
              borderRadius: '8px',
              marginTop: '16px'
            }}>
              <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', display: 'block' }}>
                Current Stats (Read-only) {formData.agentType === 'group' && formData.ghlLocationId && formData.ghlPrivateToken && '🌐'}
              </label>
              
              {/* For Groups: Show GHL Count Prominently */}
              {formData.agentType === 'group' && formData.ghlLocationId && formData.ghlPrivateToken ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Contacts in GHL</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0891b2' }}>
                      {loadingGHLCount ? '⏳ Loading...' : (ghlContactCount ?? '0')}
                    </div>
                    <div style={{ fontSize: '10px', color: '#10b981', marginTop: '4px', fontWeight: '500' }}>
                      ✓ DB-cached (sync via Admin → Sync GHL Counts)
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Database Total Sent</div>
                    <div style={{ fontSize: '18px', fontWeight: '600' }}>
                      {formData.distributionConfig.totalSent || 0}
                    </div>
                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>
                      cached from DB
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Purchased Limit</div>
                    <div style={{ fontSize: '18px', fontWeight: '600' }}>
                      {formData.distributionConfig.totalPurchased || 0}
                    </div>
                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>
                      from config
                    </div>
                  </div>
                </div>
              ) : (
                /* For Individuals: Show distribution config stats */
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Daily Sent</div>
                    <div style={{ fontSize: '18px', fontWeight: '600' }}>
                      {formData.distributionConfig.dailySent || 0} / {formData.distributionConfig.dailyCap || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Monthly Sent</div>
                    <div style={{ fontSize: '18px', fontWeight: '600' }}>
                      {formData.distributionConfig.monthlySent || 0} / {formData.distributionConfig.monthlyCap || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Sent</div>
                    <div style={{ fontSize: '18px', fontWeight: '600' }}>
                      {loadingGHLCount ? (
                        '⏳ Loading...'
                      ) : ghlContactCount !== null ? (
                        <>
                          {ghlContactCount} / {formData.distributionConfig.totalPurchased || 0}
                        </>
                      ) : (
                        <>
                          {formData.distributionConfig.totalSent || 0} / {formData.distributionConfig.totalPurchased || 0}
                        </>
                      )}
                    </div>
                    {ghlContactCount !== null && (
                      <div style={{ fontSize: '10px', color: '#10b981', marginTop: '2px', fontWeight: '500' }}>
                        ✓ DB-cached (sync via Admin → Sync GHL Counts)
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* States & Carriers - Only for Individual Agents */}
        {formData.agentType !== 'group' && (
          <div className="form-group">
            <div className="states-header">
              <label>States & Carriers</label>
              <button 
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => setShowAddState(!showAddState)}
              >
                {showAddState ? '✕ Cancel' : '+ Add State'}
              </button>
            </div>

          {showAddState && (
            <div className="add-state-form">
              <Select
                options={stateOptions}
                value={selectedState}
                onChange={setSelectedState}
                placeholder="Select a state..."
              />
              
              {selectedState && (
                <div style={{ marginTop: '12px' }}>
                  <Select
                    isMulti
                    options={carrierOptions}
                    value={groupCarriersByName(newStateCarriers).map(group => ({
                      value: group,
                      label: `${group.name} (${group.carrierIds.length})`
                    }))}
                    onChange={(selected) => {
                      if (!selected || selected.length === 0) {
                        setNewStateCarriers([]);
                      } else {
                        const newCarriers = [];
                        selected.forEach(item => {
                          item.value.carrierIds.forEach(carrierId => {
                            const carrier = carriers.find(c => c.CarrierId === carrierId);
                            if (carrier) {
                              newCarriers.push(carrier);
                            }
                          });
                        });
                        setNewStateCarriers(newCarriers);
                      }
                    }}
                    placeholder="Search and select carriers..."
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={addNewState}
                    disabled={!selectedState || newStateCarriers.length === 0}
                    style={{ marginTop: '8px' }}
                  >
                    ✓ Add {selectedState.value} with {newStateCarriers.length} carrier{newStateCarriers.length !== 1 ? 's' : ''}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ⭐ ACTIVE STATES summary banner — only shown in edit mode when some states paused */}
          {isEdit && formData.activeStates && formData.activeStates.length < Object.keys(formData.statesWithCarriers).length && (
            <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '13px', color: '#92400e', fontWeight: '600' }}>
              ⏸ {Object.keys(formData.statesWithCarriers).length - formData.activeStates.length} state(s) paused — use checkboxes below to resume
            </div>
          )}

          <div className="states-list">
            {Object.keys(formData.statesWithCarriers).length === 0 ? (
              <div className="empty-state-message">
                No states added yet. Click "+ Add State" to begin.
              </div>
            ) : (
              Object.entries(formData.statesWithCarriers).map(([state, stateCarriers]) => {
                const isActive = !formData.activeStates || formData.activeStates.includes(state);
                return (
                  <div key={state} style={{ position: 'relative', opacity: isActive ? 1 : 0.55, transition: 'opacity 0.2s' }}>
                    {/* Active/Paused toggle — only show in edit mode */}
                    {isEdit && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontSize: '13px', fontWeight: '600', color: isActive ? '#059669' : '#dc2626' }}>
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => toggleStateActive(state)}
                            style={{ width: '15px', height: '15px', accentColor: '#059669', cursor: 'pointer' }}
                          />
                          {isActive ? '✅ Active' : '⏸ Paused'}
                        </label>
                        {!isActive && (
                          <span style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                            Not receiving leads for this state
                          </span>
                        )}
                      </div>
                    )}
                    <StateCard
                      stateCode={state}
                      carriers={stateCarriers}
                      allCarriers={carriers}
                      onUpdate={updateStateCarriers}
                      onDelete={deleteState}
                      isEditing={editingState === state}
                      setIsEditing={(editing) => setEditingState(editing ? state : null)}
                    />
                  </div>
                );
              })
            )}
          </div>
          </div>
        )}

        <div className="form-footer">
          <div style={{ flex: 1 }}>
            {formData.agentType === 'group' && (
              <div style={{ 
                padding: '8px 12px',
                background: formData.groupMembers && formData.groupMembers.length > 0 ? '#d1fae5' : '#fee2e2',
                color: formData.groupMembers && formData.groupMembers.length > 0 ? '#065f46' : '#991b1b',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                display: 'inline-block'
              }}>
                {formData.groupMembers && formData.groupMembers.length > 0 
                  ? `✓ ${formData.groupMembers.length} member(s) ready to save` 
                  : '⚠️ No members added yet'}
              </div>
            )}
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/agents')}>
            ← Back
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving 
              ? (isEdit ? 'Saving...' : 'Creating...') 
              : (isEdit 
                  ? 'Save Changes' 
                  : (formData.agentType === 'group' ? 'Create Group' : 'Create Agent')
                )
            }
          </button>
        </div>
      </form>
    </div>
  );
}

export default AgentForm;
